import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  GitCompare,
  Play,
  Plus,
  Trash2,
  Save,
  Dice5,
  Clock,
  TrendingUp,
  Banknote,
  Target,
  RotateCcw,
} from "lucide-react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { Lock, Crown, Columns3 } from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, formatApiError } from "../lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import DateField from "@/components/ui/DateField";

const EMPTY_OVERRIDES = {
  retirement_age: null,
  inflation_pct: null,
  add_sip: [],
  pause_sip: [],
  sells: [],
  prepay_loans: [],
  goal_changes: [],
  new_goals: [],
  buys: [],
};

export default function Scenarios() {
  const { user } = useAuth();
  const isPremium = (user?.plan || "free").toLowerCase() !== "free";
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [goals, setGoals] = useState([]);
  const [saved, setSaved] = useState([]);

  const [overrides, setOverrides] = useState(EMPTY_OVERRIDES);
  const [includeMC, setIncludeMC] = useState(false);
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const loadInit = async () => {
    try {
      const [a, l, g, s] = await Promise.all([
        api.get("/assets?include_insurance=false"),
        api.get("/liabilities"),
        api.get("/goals"),
        api.get("/scenarios"),
      ]);
      setAssets((a.data.assets || []).filter((x) => !x.synthetic));
      setLiabilities((l.data.liabilities || []).filter((x) => !x.synthetic));
      setGoals(g.data.goals || []);
      setSaved(s.data.scenarios || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    loadInit();
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(overrides) !== JSON.stringify(EMPTY_OVERRIDES),
    [overrides],
  );

  const set = (patch) => setOverrides((o) => ({ ...o, ...patch }));
  const setRow = (key, i, val) =>
    setOverrides((o) => {
      const arr = [...(o[key] || [])];
      arr[i] = val;
      return { ...o, [key]: arr };
    });
  const addRow = (key, blank = {}) =>
    setOverrides((o) => ({ ...o, [key]: [...(o[key] || []), blank] }));
  const delRow = (key, i) =>
    setOverrides((o) => ({
      ...o,
      [key]: (o[key] || []).filter((_, j) => j !== i),
    }));

  const run = async (ov = overrides) => {
    setRunning(true);
    try {
      const { data } = await api.post("/scenarios/run", {
        overrides: ov,
        include_monte_carlo: includeMC,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const saveScenario = async () => {
    if (!name.trim()) return toast.error("Give the scenario a name");
    try {
      await api.post("/scenarios", { name, overrides });
      toast.success("Scenario saved");
      setName("");
      loadInit();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadScenario = async (id) => {
    try {
      const { data } = await api.get(`/scenarios/${id}`);
      const ov = { ...EMPTY_OVERRIDES, ...(data.overrides || {}) };
      setOverrides(ov);
      run(ov);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/scenarios/${id}`);
      loadInit();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            What-if lab
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Scenarios
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Pull the levers and compare against your current plan — nothing here
            ever changes your live data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              onClick={() => {
                setOverrides(EMPTY_OVERRIDES);
                setResult(null);
              }}
              className="btn-secondary !text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
          )}
          <button
            onClick={() => run()}
            disabled={running}
            className="btn-primary"
          >
            <Play className="h-4 w-4" />{" "}
            {running ? "Running…" : "Run simulation"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ---------------- Levers ---------------- */}
        <div className="lg:col-span-2 space-y-4">
          <LeverCard icon={Clock} title="Timeline & macro">
            <Row label="Retirement age">
              <select
                value={overrides.retirement_age ?? ""}
                onChange={(e) =>
                  set({
                    retirement_age: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
                className="input-soft !py-1.5"
              >
                <option value="">No change</option>
                {[50, 55, 58, 60, 62, 65, 70].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Inflation override %">
              <input
                type="number"
                step="0.1"
                placeholder="Default"
                value={overrides.inflation_pct ?? ""}
                onChange={(e) =>
                  set({
                    inflation_pct: e.target.value
                      ? parseFloat(e.target.value)
                      : null,
                  })
                }
                className="input-soft !py-1.5"
              />
            </Row>
          </LeverCard>

          <LeverCard icon={TrendingUp} title="Contributions">
            <ArrayLever
              rows={overrides.add_sip}
              onAdd={() => addRow("add_sip", {})}
              addLabel="Boost / add a SIP"
            >
              {(row, i) => (
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                  <select
                    value={row.asset_id || ""}
                    onChange={(e) =>
                      setRow("add_sip", i, { ...row, asset_id: e.target.value })
                    }
                    className="input-soft !py-1.5 !text-xs"
                  >
                    <option value="">New SIP (equity)</option>
                    {assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="₹/mo"
                    value={row.amount || ""}
                    onChange={(e) =>
                      setRow("add_sip", i, {
                        ...row,
                        amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="input-soft !py-1.5 !text-xs w-24"
                  />
                  <DelBtn onClick={() => delRow("add_sip", i)} />
                </div>
              )}
            </ArrayLever>

            <ArrayLever
              rows={overrides.pause_sip}
              onAdd={() => addRow("pause_sip", {})}
              addLabel="Pause a SIP"
            >
              {(row, i) => (
                <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                  <select
                    value={row.asset_id || ""}
                    onChange={(e) =>
                      setRow("pause_sip", i, {
                        ...row,
                        asset_id: e.target.value,
                      })
                    }
                    className="input-soft !py-1.5 !text-xs"
                  >
                    <option value="">— select asset —</option>
                    {assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <DelBtn onClick={() => delRow("pause_sip", i)} />
                </div>
              )}
            </ArrayLever>
          </LeverCard>

          <LeverCard icon={Banknote} title="Liquidity events">
            <ArrayLever
              rows={overrides.sells}
              onAdd={() => addRow("sells", {})}
              addLabel="Sell an asset"
            >
              {(row, i) => (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                    <select
                      value={row.asset_id || ""}
                      onChange={(e) =>
                        setRow("sells", i, { ...row, asset_id: e.target.value })
                      }
                      className="input-soft !py-1.5 !text-xs"
                    >
                      <option value="">— select asset —</option>
                      {assets.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                    <DelBtn onClick={() => delRow("sells", i)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Units (blank=all)"
                      value={row.units || ""}
                      onChange={(e) =>
                        setRow("sells", i, {
                          ...row,
                          units: parseFloat(e.target.value) || null,
                        })
                      }
                      className="input-soft !py-1.5 !text-xs"
                    />
                    <input
                      type="number"
                      placeholder="Total proceeds ₹"
                      value={row.sale_proceeds || ""}
                      onChange={(e) =>
                        setRow("sells", i, {
                          ...row,
                          sale_proceeds: parseFloat(e.target.value) || null,
                        })
                      }
                      className="input-soft !py-1.5 !text-xs"
                    />
                  </div>
                </div>
              )}
            </ArrayLever>

            <ArrayLever
              rows={overrides.prepay_loans}
              onAdd={() => addRow("prepay_loans", {})}
              addLabel="Prepay a loan"
            >
              {(row, i) => (
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                  <select
                    value={row.liability_id || ""}
                    onChange={(e) =>
                      setRow("prepay_loans", i, {
                        ...row,
                        liability_id: e.target.value,
                      })
                    }
                    className="input-soft !py-1.5 !text-xs"
                  >
                    <option value="">— select loan —</option>
                    {liabilities.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="₹"
                    value={row.amount || ""}
                    onChange={(e) =>
                      setRow("prepay_loans", i, {
                        ...row,
                        amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="input-soft !py-1.5 !text-xs w-24"
                  />
                  <DelBtn onClick={() => delRow("prepay_loans", i)} />
                </div>
              )}
            </ArrayLever>
          </LeverCard>

          <LeverCard icon={Target} title="Goals">
            <ArrayLever
              rows={overrides.goal_changes}
              onAdd={() => addRow("goal_changes", {})}
              addLabel="Modify a goal"
            >
              {(row, i) => (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                    <select
                      value={row.goal_id || ""}
                      onChange={(e) =>
                        setRow("goal_changes", i, {
                          ...row,
                          goal_id: e.target.value,
                        })
                      }
                      className="input-soft !py-1.5 !text-xs"
                    >
                      <option value="">— select goal —</option>
                      {goals.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    <DelBtn onClick={() => delRow("goal_changes", i)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <DateField
                      value={row.target_date || ""}
                      onChange={(v) =>
                        setRow("goal_changes", i, {
                          ...row,
                          target_date: v,
                        })
                      }
                    />
                    <input
                      type="number"
                      placeholder="New target ₹"
                      value={row.target_amount || ""}
                      onChange={(e) =>
                        setRow("goal_changes", i, {
                          ...row,
                          target_amount: parseFloat(e.target.value) || null,
                        })
                      }
                      className="input-soft !py-1.5 !text-xs"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-slate-500">
                    <Checkbox
                      checked={!!row.drop}
                      onCheckedChange={(checked) =>
                        setRow("goal_changes", i, {
                          ...row,

                          drop: checked === true,
                        })
                      }
                    />

                    <span>Drop this goal entirely</span>
                  </label>
                </div>
              )}
            </ArrayLever>
          </LeverCard>

          {/* Run options + save */}
          <div className="card-soft p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Checkbox
                checked={includeMC}
                onCheckedChange={(checked) => setIncludeMC(checked === true)}
              />

              <span>Include Monte Carlo (1,000 random paths)</span>
            </label>
            <div className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Save this scenario as…"
                className="input-soft !py-2 flex-1"
              />
              <button onClick={saveScenario} className="btn-secondary">
                <Save className="h-4 w-4" /> Save
              </button>
            </div>
            {saved.length > 0 && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Saved scenarios
                </div>
                <ul className="space-y-1">
                  {saved.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <button
                        onClick={() => loadScenario(s.id)}
                        className="hover:text-emerald-600 truncate text-left flex-1"
                      >
                        {s.name}
                      </button>
                      <DelBtn onClick={() => deleteScenario(s.id)} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* ---------------- Results ---------------- */}
        <div className="lg:col-span-3 space-y-4">
          {!result ? (
            <div className="card-soft p-12 text-center text-slate-500">
              <GitCompare className="h-12 w-12 mx-auto mb-3 opacity-30" />
              Configure levers on the left and click <b>Run simulation</b> to
              compare against your current plan.
            </div>
          ) : (
            <Results result={result} />
          )}

          <ScenarioCompare saved={saved} isPremium={isPremium} />
        </div>
      </div>
    </DashboardLayout>
  );
}

const fmtMetric = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "pct") return `${Math.round(v)}%`;
  if (fmt === "years") return `${v} yrs`;
  return fmtINR(v, { compact: true });
};

function ScenarioCompare({ saved, isPremium }) {
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/scenarios/compare", {
        scenario_ids: selected,
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!isPremium) {
    return (
      <div className="card-soft p-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Compare scenarios
              </h3>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white">
                Premium
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Put your current plan and saved scenarios side by side — net worth,
              retirement, taxes and FI — and see which wins on each measure.
            </p>
            <Link to="/dashboard/settings" className="btn-primary !text-xs mt-4 inline-flex">
              <Crown className="h-4 w-4" /> Unlock with Premium
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-soft p-6" data-testid="scenario-compare">
      <div className="flex items-start gap-2 mb-4">
        <Columns3 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            Compare scenarios
          </h3>
          <p className="text-xs text-slate-500">
            Pick saved scenarios to line up against your current plan.
          </p>
        </div>
      </div>

      {saved.length === 0 ? (
        <p className="text-sm text-slate-500">
          Save a scenario first (left panel), then compare it here.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {saved.map((s) => {
              const on = selected.includes(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                    on
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
          <button
            onClick={run}
            disabled={loading || selected.length === 0}
            className="btn-primary !text-xs mt-3 disabled:opacity-40"
          >
            {loading ? "Comparing…" : `Compare ${selected.length || ""}`.trim()}
          </button>
        </>
      )}

      {data && data.columns?.length > 1 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left">
                <th className="py-2 pr-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Metric
                </th>
                {data.columns.map((c) => (
                  <th
                    key={c.id}
                    className="py-2 px-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap"
                  >
                    {c.name}
                    {c.is_baseline && (
                      <span className="ml-1 text-[9px] uppercase tracking-wide text-slate-400">
                        base
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.metrics.map((m) => {
                const vals = data.columns.map((c) => c.metrics[m.key]);
                const nums = vals.filter((v) => v != null);
                const best =
                  nums.length > 1
                    ? m.better === "low"
                      ? Math.min(...nums)
                      : Math.max(...nums)
                    : null;
                return (
                  <tr key={m.key} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-3 text-slate-500">{m.label}</td>
                    {data.columns.map((c) => {
                      const v = c.metrics[m.key];
                      const isBest = best != null && v === best && nums.length > 1;
                      return (
                        <td
                          key={c.id}
                          className={`py-2 px-3 text-right num-mono whitespace-nowrap ${
                            isBest
                              ? "text-emerald-600 font-bold"
                              : "text-slate-800 dark:text-slate-100"
                          }`}
                        >
                          {fmtMetric(v, m.fmt)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-slate-400">
            Green = best on that row. {data.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
}

function Results({ result }) {
  const { baseline, scenario, deltas, sale_events, monte_carlo } = result;
  const chartData = mergeSeries(
    baseline?.net_worth_path,
    scenario?.net_worth_path,
  );
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Delta label="Net Worth (now)" v={deltas?.total_net_worth} />
        <Delta label="Net of Tax" v={deltas?.net_of_tax_total} />
        <Delta
          label="Retirement Corpus"
          v={deltas?.retirement_projected_corpus}
        />
        <Delta
          label="Years to FI"
          v={deltas?.fi_years}
          suffix=" yrs"
          rupee={false}
          invert
        />
      </div>

      <div className="card-soft p-6">
        <h3 className="font-display font-bold mb-1">
          Net worth — current vs scenario
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          20-year coherent projection.
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis
              tickFormatter={(v) => fmtINR(v, { compact: true })}
              tick={{ fontSize: 11 }}
            />
            <Tooltip formatter={(v) => fmtINR(v)} />
            <Legend />
            <Line
              type="monotone"
              dataKey="baseline"
              stroke="#94a3b8"
              strokeWidth={2}
              dot={false}
              name="Current plan"
            />
            <Line
              type="monotone"
              dataKey="scenario"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={false}
              name="Scenario"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Retirement / FI compare */}
      <div className="grid sm:grid-cols-2 gap-4">
        <CompareCard
          title="Retirement"
          rows={[
            [
              "Projected corpus",
              baseline?.retirement?.projected_corpus,
              scenario?.retirement?.projected_corpus,
            ],
            [
              "Gap",
              baseline?.retirement?.retirement_gap,
              scenario?.retirement?.retirement_gap,
            ],
          ]}
        />
        <CompareCard
          title="Financial Independence"
          rows={[
            [
              "FI number",
              baseline?.financial_independence?.fi_number,
              scenario?.financial_independence?.fi_number,
            ],
            [
              "Years to FI",
              baseline?.financial_independence?.years_to_fi,
              scenario?.financial_independence?.years_to_fi,
              true,
            ],
          ]}
        />
      </div>

      {sale_events?.length > 0 && (
        <div className="card-soft p-6">
          <h3 className="font-display font-bold mb-3">Sale tax events</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="text-left">Class</th>
                <th className="text-right">Gain</th>
                <th className="text-right">Tax</th>
                <th className="text-right">Net proceeds</th>
              </tr>
            </thead>
            <tbody>
              {sale_events.map((e, i) => (
                <tr
                  key={i}
                  className="border-t border-slate-100 dark:border-slate-800"
                >
                  <td className="py-2">{e.asset_class}</td>
                  <td className="text-right">
                    {fmtINR(e.realized_gain, { compact: true })}
                  </td>
                  <td className="text-right text-amber-600">
                    {fmtINR(e.tax, { compact: true })}
                  </td>
                  <td className="text-right text-emerald-600 font-medium">
                    {fmtINR(e.net_proceeds, { compact: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {monte_carlo && (
        <div className="card-soft p-6">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2">
            <Dice5 className="h-5 w-5 text-indigo-600" /> Monte Carlo (
            {monte_carlo.paths} paths)
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            {monte_carlo.disclaimer}
          </p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
            {["p10", "p25", "p50", "p75", "p90"].map((k) => (
              <div
                key={k}
                className={`rounded-lg p-2 ${k === "p50" ? "bg-indigo-50 dark:bg-indigo-900/30" : "bg-slate-50 dark:bg-slate-800/50"}`}
              >
                <div className="text-[10px] uppercase text-slate-500">
                  {k === "p50" ? "Median" : k.toUpperCase()}
                </div>
                <div className="text-sm font-semibold">
                  {fmtINR(monte_carlo.final_net_worth[k], { compact: true })}
                </div>
              </div>
            ))}
          </div>
          {monte_carlo.yearly_percentiles?.length > 0 && (
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={monte_carlo.yearly_percentiles}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => fmtINR(v, { compact: true })}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Area
                  dataKey="p90"
                  stroke="#6366f1"
                  fill="#6366f1"
                  fillOpacity={0.12}
                  name="P10–P90"
                />
                <Area
                  dataKey="p10"
                  stroke="#6366f1"
                  fill="#ffffff"
                  fillOpacity={1}
                  legendType="none"
                />
                <Line
                  dataKey="p50"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  dot={false}
                  name="Median"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </>
  );
}

/* ---------------- small components ---------------- */
function LeverCard({ icon: Icon, title, children }) {
  return (
    <div className="card-soft p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          {title}
        </h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="w-40">{children}</div>
    </div>
  );
}

function ArrayLever({ rows, onAdd, addLabel, children }) {
  return (
    <div className="space-y-2">
      {(rows || []).map((row, i) => (
        <div
          key={i}
          className="rounded-lg border border-slate-200 dark:border-slate-700 p-2"
        >
          {children(row, i)}
        </div>
      ))}
      <button
        onClick={onAdd}
        className="text-xs text-emerald-600 flex items-center gap-1 font-medium"
      >
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

function DelBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-slate-400 hover:text-rose-500 p-1"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function Delta({ label, v, suffix = "", rupee = true, invert = false }) {
  const val = v ?? 0;
  // For "years to FI", a negative delta (fewer years) is good → invert colour.
  const good = invert ? val <= 0 : val >= 0;
  const display = rupee
    ? fmtINR(val, { compact: true })
    : `${val > 0 ? "+" : ""}${val}${suffix}`;
  return (
    <div className="card-soft p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div
        className={`text-lg font-bold num-mono ${good ? "text-emerald-600" : "text-rose-500"}`}
      >
        {rupee && val > 0 ? "+" : ""}
        {display}
      </div>
    </div>
  );
}

function CompareCard({ title, rows }) {
  return (
    <div className="card-soft p-5">
      <h3 className="font-display font-bold mb-3">{title}</h3>
      <div className="space-y-2">
        {rows.map(([label, base, scen, isYears], i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-slate-500 text-xs">{label}</span>
            <div className="flex items-center gap-2 num-mono">
              <span className="text-slate-400">{fmt(base, isYears)}</span>
              <span className="text-slate-300">→</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {fmt(scen, isYears)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmt(v, isYears) {
  if (v === null || v === undefined) return "—";
  return isYears ? `${v} yrs` : fmtINR(v, { compact: true });
}

function mergeSeries(b, s) {
  const map = new Map();
  (b || []).forEach((r) =>
    map.set(r.year, { year: r.year, baseline: r.net_worth }),
  );
  (s || []).forEach((r) =>
    map.set(r.year, {
      ...(map.get(r.year) || { year: r.year }),
      scenario: r.net_worth,
    }),
  );
  return Array.from(map.values()).sort((a, b) => a.year - b.year);
}
